import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BlockIcon from "@mui/icons-material/Block";
import { ListQuery } from "../../../Types/CommonTypes";
import { LocationViewModel } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../../../components/ResponsivePagination";
const LocationList: React.FC = () => {
    const [locations, setLocations] = useState<LocationViewModel[]>([]);
    const { t } = useTranslation();

    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
    });
    const [filter, setFilter] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const fetchLocations = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: LocationViewModel[]; count: number }>(
                "/settingsApi/GetLocationList",
                query
            );
            setLocations(response.items);
            setTotalCount(response.count);
        } catch (error : any) {
            showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLocations();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (code?: string | null) => {
        navigate({ path: "/settings/location/edit", params: { code } });
    };

    const handleCreate = () => {
        navigate({ path: "/settings/location/edit", params: { code: "" } });
    };

    return (
        <CenteredListCard title={t("common.locations")}>
     
            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreate}
                >
                    {t("common.create")}
                </Button>
            </Box>
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <TextField
                        label={t("common.search")}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => setFilter(event.target.value)}
                    value={filter}
                    disabled={loading}
                />
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                >
                        {t("common.search")}
                </Button>
            </Box>


            <Grid container spacing={2} sx={{mb:2} }>
                {locations.map((location) => (
                    <Grid item
                        xs={12}
                        sm={locations.length === 1 ? 12 : locations.length === 2 ? 6 : 6}
                        md={locations.length === 1 ? 12 : locations.length === 2 ? 6 : 4}
                        key={location.code}>
                        <Card
                            onClick={() => handleSelect(location.code)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<LocationOnIcon fontSize="small" color="action" />}
                                    title={location.description}
                                    status={location.blocked
                                        ? {
                                            label: t("common.blocked"),
                                            color: "warning",
                                            icon: <BlockIcon fontSize="small" />,
                                        }
                                        : undefined}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.code")}: {location.code}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.blocked")}: {location.blocked ? t("common.yes") : t("common.no")}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {!loading && locations.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center">  {t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalCount}

                onPageChange={handlePageChange}

            />
 
        </CenteredListCard>

    );
};

export default LocationList;
